"use client";
import React from "react";
import Link from "next/link";
import { Card, Badge } from "react-bootstrap";
import { Calendar, Clock, Eye } from "lucide-react";
import styles from "./BlogCard.module.css";
import { BlogPost } from "@/app/types/blog";
import Image from "next/image";
import { getAuthorAvatar } from "@/app/utils/authorImage";

export const FeaturedBlogCard: React.FC<{ post: BlogPost }> = ({ post }) => {
  const slug =
    post.slug ||
    post.title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  // ✅ Handle flexible author types
  const authorName =
    typeof post.author === "object"
      ? post.author.name
      : post.author || "Unknown";

  // Use local author images from /public/blog/authorImages/
  const fallbackAvatar =
    typeof post.author === "object"
      ? post.author.avatar
      : post.avatar || "https://i.pravatar.cc/100?img=11";

  const authorAvatar = getAuthorAvatar(authorName, fallbackAvatar);
  const featuredImage =
    post.image || "https://placehold.co/1200x630/0b1d51/FFFFFF?text=Temok+Blog";

  return (
    <div className={`${styles.featuredCard} border-0 rounded-5`}>
      <Link href={`/blog/${slug}`} className="text-decoration-none">
        <div className={styles.featuredImageWrapper}>
          {/* ---------- Fixed Main Blog Image ---------- */}
          <Card.Img
            src={featuredImage}
            alt={post.title}
            className={styles.featuredImage}
          />
          <div className={styles.featuredOverlay}></div>

          {/* ---------- Overlay Content ---------- */}
          <div className={styles.featuredText}>
            {/* Category Badge */}
            <Badge className={styles.featuredCategory}>{post.category}</Badge>

            {/* Title */}
            <Card.Title className={styles.featuredTitle}>
              {post.title}
            </Card.Title>

            {/* Excerpt */}
            <Card.Text className={`${styles.excerpt} text-white`}>
              {post.excerpt}
            </Card.Text>

            {/* ---------- Author + Meta Info ---------- */}
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mt-3">
              {/* Author Info */}
              <div className="d-flex align-items-center gap-2">
                <Image
                  src={authorAvatar}
                  alt={authorName}
                  width={50}
                  height={50}
                  className="rounded-circle border border-light"
                />
                <span className="fw-semibold text-white">{authorName}</span>
              </div>

              {/* Date + Read Time + Views */}
              <div className="d-flex align-items-center gap-3 text-white small">
                <div className="d-flex align-items-center gap-1">
                  <Calendar size={16} />
                  <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
                </div>
                <div className="d-flex align-items-center gap-1">
                  <Clock size={16} />
                  <span>{post.readTime}</span>
                </div>
                {/* <div className="d-flex align-items-center gap-1">
                  <Eye size={16} />
                  <span>{post.views.toLocaleString()}</span>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};
